import React, { useState, useMemo, useContext } from 'react';
import { BulkUploadButton, ErrorMessage, TempDetails } from '../TemplateSection/styles';
import {
  PDF_TRANSACTIONS,
  TRANSACTIONS,
} from '../TemplateSection/translationValues';
import { withTranslation } from 'react-i18next';
import { Icon, Upload, message, Button, Dropdown, Menu } from 'antd';
import * as XLSX from 'xlsx';
import { VARIABLE_TYPE } from '../../utils/constants/appConstants';
import { SUPPORT_EMAIL_ID } from '../../utils/helper';
import {
  uploadComplexEmailDataSet,
} from '../../utils/services/complexEmailAPI';
import VariableDetailsModal from '../../components/Modals/VariableDetailsModal';
import UpgradePlanModal from '../../components/Modals/UpgradePlanModal';
import { AppStoreContext } from '../../context/appStoreContext';
import { getPayloadFromFile, getXMLFromJSON } from '../../utils/transactionHelpers';
import { xmlToFile,jsonToFile } from '../../utils/javascript';
import { getSampleDataSet, generateCSVHeadersFromJSON, downloadDelimitedFile, generatePayloadFromCsvFile, parseFileWithKnownDelimiters } from '../../utils/helper';
import { checkFileFormat } from '../../utils/transactionHelpers';
import { dynamicTableHasMoreThanTwoLevels } from '../ComplexDocumentDesign/complexDocumentHelper';

const UploadSection = props => {
  const { t, selectedTemplate } = props;

  const [isUploading, setIsUploading] = useState(false);
  const [isViewVarDetails, setIsViewVarDetails] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const { userDetails, globalVariables } = useContext(AppStoreContext);
  const { isEmailTransactionFull } = userDetails;
  const { planName, emailTransactionLimit } = userDetails.planMapping;

  const [isRepeatedBlocks, isChartBlock] = useMemo(() => {
    if (selectedTemplate?.variableContract?.length > 0) {
      const hasRepeatedBlocks = selectedTemplate?.variableContract?.some(
        variable => variable.type === VARIABLE_TYPE.OBJECT,
      );
      const hasChartBlock = selectedTemplate?.variableContract?.some(
        variable => variable.type === VARIABLE_TYPE.CHART_OBJECT,
      );
      return [hasRepeatedBlocks, hasChartBlock];
    }
    return [false, false];
  }, [selectedTemplate]);

  const apiArrayToExcel = async () => {
    const wb = XLSX.utils.book_new();
    // Create a worksheet
    const ws = XLSX.utils.aoa_to_sheet([]);
    // Add data to worksheet
    let col = 0;
    if (isRepeatedBlocks || isChartBlock) {
      XLSX.utils.sheet_add_aoa(ws, [['id']], {
        origin: { r: 0, c: col++ },
      });
    }
    XLSX.utils.sheet_add_aoa(ws, [['uniq_id']], {
      origin: { r: 0, c: col++ },
    });
    XLSX.utils.sheet_add_aoa(ws, [['uniq_name']], {
      origin: { r: 0, c: col++ },
    });
    const header = ['To Email Address', 'CC'];
    if (selectedTemplate?.passwordProtected ?? false) {
      header.push('Attachment Password');
    }
    header.forEach(item => {
      XLSX.utils.sheet_add_aoa(ws, [[item]], {
        origin: { r: 0, c: col },
      });
      col++;
    });
    // Add main headers and subheaders
    for (const item of selectedTemplate?.variableContract ?? []) {
      if (
        item.type.toLowerCase() === VARIABLE_TYPE.STRING ||
        item.type === VARIABLE_TYPE.LIST_OF_STRINGS ||
        item.type === 'date'|| item.type === VARIABLE_TYPE.HTML
      ) {
        XLSX.utils.sheet_add_aoa(ws, [[item.name]], {
          origin: { r: 0, c: col },
        });
        col++;
      } else if (item.type === VARIABLE_TYPE.OBJECT || item.type === VARIABLE_TYPE.CHART_OBJECT) {
        item.content.forEach(subItem => {
          XLSX.utils.sheet_add_aoa(ws, [[item.name]], {
            origin: { r: 0, c: col },
          });
          XLSX.utils.sheet_add_aoa(ws, [[subItem.name]], {
            origin: { r: 1, c: col },
          });

          col++;
        });
      }
    }
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    // Generate Excel file
    XLSX.writeFile(wb, `${selectedTemplate?.name}.xlsx`);
    return;
  };

  const handleUploadDataSet = async (convertedData, templateName) => {
    setIsUploading(true);
    const loadingInfo = message.loading(
      t(
        TRANSACTIONS.PROCESSING_REQUEST_INFO_MESSAGE.key,
        TRANSACTIONS.PROCESSING_REQUEST_INFO_MESSAGE.value,
      ),
      0,
    );
    try {
      const response = await uploadComplexEmailDataSet(templateName, convertedData);

      // On API time-out
      if (!response) {
        message.error(
          t(
            PDF_TRANSACTIONS.TIME_OUT_ERROR_MESSAGE.key,
            PDF_TRANSACTIONS.TIME_OUT_ERROR_MESSAGE.value,
          ),
        );
      }
      // 100% Success
      else if (response && (!response.errors || response.errors.length === 0)) {
        message.success(t(TRANSACTIONS.REQUEST_SUBMIT_SUCCESS_MESSAGE.key, TRANSACTIONS.REQUEST_SUBMIT_SUCCESS_MESSAGE.value));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsUploading(false);
      loadingInfo();
    }
  };

  const getFlatFilesPayload = async (file) => {
    const { data, delimiter } = await parseFileWithKnownDelimiters(file);
    const payload = generatePayloadFromCsvFile(data, selectedTemplate.variableContract, 'live/email', selectedTemplate.id);
    return payload;
  }

  const beforeUploadEmailTransaction = async file => {
    if (file) {
      const fileFormat = checkFileFormat(file.name) ? 'excel' : file.type;
      if (fileFormat === 'text/plain' || fileFormat === 'text/csv') {
        const payload = await getFlatFilesPayload(file);
        if (payload && payload?.length > 0) {
          handleUploadDataSet(payload);
        }
        else {
          message.error(
            t(
              TRANSACTIONS.PROVIDED_DATA_INVALID_ERROR_MESSAGE.key,
              TRANSACTIONS.PROVIDED_DATA_INVALID_ERROR_MESSAGE.value,
            ),
          );
        }
        setIsUploading(false);
        return;
      }
    }
    const reader = new FileReader();
    reader.onload = async event => {
      const binaryString = event.target.result;
      const requests = getPayloadFromFile(binaryString, file, selectedTemplate, t, true);

      setIsUploading(false);

      if (requests === false) {
        message.error(
          t(
            `TRANSACTIONS:${TRANSACTIONS.WRONG_FILE_UPLOADED_ERROR_MESSAGE.key}`,
            TRANSACTIONS.WRONG_FILE_UPLOADED_ERROR_MESSAGE.value,
          ),
        );
        return;
      }
      if (requests) {
        handleUploadDataSet(requests.map(req => Object.assign({}, req, { mailTemplateId: selectedTemplate?.id })), selectedTemplate?.name);
      }
    };
    reader.readAsBinaryString(file);
    return false; // Prevent default upload behavior
  };

  const getSampleValue = (variableId) => {
    const samplevalue = globalVariables.find(list => list.id === variableId);
    if (samplevalue) {
      return samplevalue.exampleValue;
    }
    return '';
  };

  const downloadFlatFiles = (format) => {
    if (selectedTemplate?.variableContract.length > 0) {
      const { hasMoreThanTwoLevels } = dynamicTableHasMoreThanTwoLevels(selectedTemplate.variableContract);
      if (hasMoreThanTwoLevels) {
        message.warning(t(
          TRANSACTIONS.WARNING_MESSAGE_SAMPLE_EXCEL_DOWNLOAD.key,
          TRANSACTIONS.WARNING_MESSAGE_SAMPLE_EXCEL_DOWNLOAD.value,
        ));
        return;
      }
    }
    const dataset = getSampleDataSet(selectedTemplate.variableContract, globalVariables, 'flatFile','live',true);
    const headers = generateCSVHeadersFromJSON(dataset);
    const templateName = selectedTemplate['name'];
    downloadDelimitedFile({ templateName,headers, format: format });
    return;
  }

  const downloadSampleFormat = format => {
    let dataSet = { };
    selectedTemplate.variableContract.forEach(variable => {  
      dataSet = {
        ...dataSet,
        uniq_id:'',
        uniq_name:''
      };    
      if (variable.type === VARIABLE_TYPE.OBJECT) {
        const result = {};

        variable.content.forEach(contentItem => {
          if (contentItem.type === VARIABLE_TYPE.CHART_OBJECT) {
            const chartObject = contentItem.content.reduce((acc, contentItem) => {
              acc[contentItem.name] = getSampleValue(contentItem.variableId);
              return acc;
            }, {});

            result[contentItem.name] = [chartObject];
          } else {
            variable.content.forEach(nonChartContentItem => {
              result[nonChartContentItem.name] = getSampleValue(nonChartContentItem.variableId) || [
                {},
              ];
            });
          }
        });

        dataSet = {
          ...dataSet,
          [variable.name]: [result],
        };      
      } else if (variable.type === VARIABLE_TYPE.CHART_OBJECT) {
        const chartObject = variable.content.reduce((acc, contentItem) => {
          acc[contentItem.name] = getSampleValue(contentItem.variableId);
          return acc;
        }, {});

        dataSet = {
          ...dataSet,
          [variable.name]: [chartObject],
        };
      } else {
        const samplevalue = getSampleValue(variable.variableId);
        dataSet = {
          ...dataSet,
          [variable.name]: variable.type === 'listOfStrings' ? [samplevalue] : samplevalue,
        };
      }
    });

    const sampleJSON = [
      {
        ...dataSet,
        toEmailAddress: 'sample@example.com',
        cc:'sample@example.com',
        attachmentPassword: 'password',
      },
    ];
    if (format === 'json') {
      jsonToFile(sampleJSON, `${selectedTemplate['name']}-sample-request`);
    } else if (format === 'xml') {
      const xmlString = getXMLFromJSON({ request: sampleJSON });
      xmlToFile(xmlString, `${selectedTemplate['name']}-sample-request`);
    }
  };

  const handleLimitExceeded = e => {
    e.stopPropagation();
    setShowUpgradeModal(true);
  };

  return (
    <>
      <ErrorMessage complexEmailTransactions={true}>
        {selectedTemplate && (
          <TempDetails>
            <div className="tempDetail">
              <div>
                {t(
                  TRANSACTIONS.SELECTED_TEMPLATE_TITLE.key,
                  TRANSACTIONS.SELECTED_TEMPLATE_TITLE.value,
                )}
                <p
                  style={{
                    paddingLeft: 0,
                  }}
                >
                  {selectedTemplate?.name}
                </p>
              </div>
              <div>
                {t(
                  TRANSACTIONS.TEMPLATE_VARIABLES_TITLE.key,
                  TRANSACTIONS.TEMPLATE_VARIABLES_TITLE.value,
                )}
                <div className="variables">
                  {selectedTemplate?.variableContract?.map(
                    (variable, index) => index < 3 && <p>{variable.name}</p>,
                  )}
                  <p
                    className={
                      selectedTemplate?.variableContract?.length === 0 ||
                      !selectedTemplate?.variableContract
                        ? 'varDetails noVar'
                        : 'varDetails'
                    }
                    onClick={() => {
                      selectedTemplate?.variableContract?.length > 0 && setIsViewVarDetails(true);
                    }}
                  >
                    {selectedTemplate?.variableContract?.length === 0 ||
                    !selectedTemplate?.variableContract
                      ? t(TRANSACTIONS.NO_VARIABLES.key, TRANSACTIONS.NO_VARIABLES.value)
                      : selectedTemplate?.variableContract?.length > 3 &&
                        t(
                          TRANSACTIONS.SEE_ALL_TEMPLATE_VARIABLES_ACTION.key,
                          TRANSACTIONS.SEE_ALL_TEMPLATE_VARIABLES_ACTION.value,
                        )}
                  </p>
                </div>
              </div>
            </div>
                <div id={'more-options-download'}>
                  <Dropdown
                    overlay={
                      <Menu>
                        <Menu.Item key="1" onClick={apiArrayToExcel}>
                          Excel
                        </Menu.Item>
                        <Menu.Item
                          key="2"
                          onClick={e => {
                            downloadSampleFormat('json');
                          }}
                        >
                          JSON
                        </Menu.Item>
                        <Menu.Item
                          key="3"
                          onClick={e => {
                            downloadSampleFormat('xml');
                          }}
                        >
                          XML
                        </Menu.Item>
                        <Menu.Item
                          key="4"
                          onClick={e => {
                            downloadFlatFiles('csv');
                          }}
                        >
                          CSV
                        </Menu.Item>
                        <Menu.Item
                          key="5"
                          onClick={e => {
                            downloadFlatFiles('tsv');
                          }}
                        >
                        TEXT (Tab Delimited)
                        </Menu.Item>
                        <Menu.Item
                          key="6"
                          onClick={e => {
                            downloadFlatFiles('psv');
                          }}
                        >
                        TEXT (Pipe Delimited)
                        </Menu.Item>
                      </Menu>
                    }
                    placement={'bottomRight'}
                    trigger={['hover']}
                    getPopupContainer={() => document.getElementById('more-options-download')}
                  >                    
                  <Button>
                    {t(
                      TRANSACTIONS.DOWNLOAD_SAMPLE_FILE_FORMAT_BUTTON.key,
                      TRANSACTIONS.DOWNLOAD_SAMPLE_FILE_FORMAT_BUTTON.value,
                    )}
                    <Icon
                      type="down"
                      style={{ paddingLeft: 10 }}
                      onClick={e => {
                        e.stopPropagation();
                      }}
                    />
                  </Button>
                  </Dropdown>
                </div>
          </TempDetails>
        )}
        <Upload
          accept=".xls, .xlsx, .json, .xml"
          showUploadList={false}
          className="varUpload"
          beforeUpload={file => beforeUploadEmailTransaction(file)}
          disabled={isUploading || isEmailTransactionFull}
        >
          <BulkUploadButton onClick={e => isEmailTransactionFull && handleLimitExceeded(e)}>            
            <p className="import">
              {t(
                TRANSACTIONS.DRAG_AND_DROP_FILE_FORMAT_TITLE.key,
                TRANSACTIONS.DRAG_AND_DROP_FILE_FORMAT_TITLE.value,
              )}
            </p>
            <p className="desc">
              {t(
                TRANSACTIONS.GENETRATE_BULK_TRANSACTIONS_DESCRIPTION.key,
                TRANSACTIONS.GENETRATE_BULK_TRANSACTIONS_DESCRIPTION.value,
              )}
            </p>
          </BulkUploadButton>
        </Upload>
      </ErrorMessage>

      <VariableDetailsModal
        visible={isViewVarDetails}
        isViewTags={false}
        isPublishedMessages={false}
        tagsData={[]}
        isMissedVarList={false}
        variables={selectedTemplate?.variableContract}
        onCancel={() => setIsViewVarDetails(false)}
        showMaxLength={false}
      />
      <UpgradePlanModal
        visible={showUpgradeModal}
        modalText={
          planName === 'Premium'
            ? t(
                TRANSACTIONS.EMAIL_PREMIUM_PLAN_UPGRADE_MESSAGE.key,
                TRANSACTIONS.EMAIL_PREMIUM_PLAN_UPGRADE_MESSAGE.value,
                { emailId: SUPPORT_EMAIL_ID },
              )
            : planName === 'Free'
            ? t(
                TRANSACTIONS.EMAIL_FREE_PLAN_UPGRADE_MESSAGE.key,
                TRANSACTIONS.EMAIL_FREE_PLAN_UPGRADE_MESSAGE.value,
                { emailTransactionLimit },
              )
            : t(
                TRANSACTIONS.EMAIL_STANDARD_PLAN_UPGRADE_MESSAGE.key,
                TRANSACTIONS.EMAIL_STANDARD_PLAN_UPGRADE_MESSAGE.value,
                { emailTransactionLimit },
              )
        }
        onCancel={() => setShowUpgradeModal(false)}
      />
    </>
  );
};

const NAMESPACES = ['TRANSACTIONS', 'EMAIL_TRANSACTIONS', 'PDF_TRANSACTIONS'];
export default withTranslation(NAMESPACES)(UploadSection);
